import { useState, useEffect, useCallback } from 'react';

const STORAGE_PREFIX = 'autopod-history-';
const MAX_ITEMS = 20;
const MIN_PROGRESS_PERCENT = 0.01; // At least 1% progress
const MAX_PROGRESS_PERCENT = 0.95; // Less than 95% complete

export interface HistoryItem {
  episodeId: number;
  episodeTitle: string;
  showTitle: string;
  image?: string;
  progress: number; // 0-1
  duration: number;
  lastPlayed: number; // Unix timestamp
}

function getStorageKey(streamId: number): string {
  return `${STORAGE_PREFIX}${streamId}`;
}

function loadHistory(streamId: number): HistoryItem[] {
  try {
    const stored = localStorage.getItem(getStorageKey(streamId));
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

function saveHistory(streamId: number, history: HistoryItem[]): void {
  try {
    localStorage.setItem(getStorageKey(streamId), JSON.stringify(history));
  } catch {
    // Ignore storage errors
  }
}

export interface UsePlaybackHistoryReturn {
  /** History items for the current stream (sorted by lastPlayed desc) */
  history: HistoryItem[];
  /** Add or update an episode in history */
  updateHistory: (item: Omit<HistoryItem, 'lastPlayed'>) => void;
  /** Remove an episode from history */
  removeFromHistory: (episodeId: number) => void;
  /** Clear all history for the current stream */
  clearHistory: () => void;
}

export function usePlaybackHistory(streamId: number | null): UsePlaybackHistoryReturn {
  const [history, setHistory] = useState<HistoryItem[]>([]);

  // Load history when stream changes
  useEffect(() => {
    if (streamId) {
      setHistory(loadHistory(streamId));
    } else {
      setHistory([]);
    }
  }, [streamId]);

  // Update an episode in history
  const updateHistory = useCallback((item: Omit<HistoryItem, 'lastPlayed'>) => {
    if (!streamId) return;

    // Only track if progress is within the valid range
    if (item.progress < MIN_PROGRESS_PERCENT || item.progress >= MAX_PROGRESS_PERCENT) {
      return;
    }

    setHistory(prev => {
      // Remove existing entry for this episode
      const filtered = prev.filter(h => h.episodeId !== item.episodeId);

      // Add new entry
      const newItem: HistoryItem = {
        ...item,
        lastPlayed: Date.now(),
      };

      // Add to start and limit to MAX_ITEMS
      const updated = [newItem, ...filtered].slice(0, MAX_ITEMS);

      // Persist
      saveHistory(streamId, updated);

      return updated;
    });
  }, [streamId]);

  // Remove an episode from history
  const removeFromHistory = useCallback((episodeId: number) => {
    if (!streamId) return;

    setHistory(prev => {
      const updated = prev.filter(h => h.episodeId !== episodeId);
      saveHistory(streamId, updated);
      return updated;
    });
  }, [streamId]);

  // Clear all history
  const clearHistory = useCallback(() => {
    if (!streamId) return;

    setHistory([]);
    saveHistory(streamId, []);
  }, [streamId]);

  // Filter out episodes that are nearly complete (> 95%)
  const activeHistory = history.filter(h => h.progress < MAX_PROGRESS_PERCENT);

  return {
    history: activeHistory,
    updateHistory,
    removeFromHistory,
    clearHistory,
  };
}
